// Layout.jsx
import React, { useState, useRef, useLayoutEffect } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import Sidebar from './Sidebar';
import Header from './Header';
import { a, useAppColorMode } from './appColors';
import './app-theme.css';

const EXPANDED_W = 205;
const COLLAPSED_W = 76;

const Layout = ({ children }) => {
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const [headerH, setHeaderH] = useState(64); // sensible fallback
  const headerRef = useRef(null);
  const { isDark, toggle } = useAppColorMode();

  const toggleSidebar = () => setSidebarOpen(v => !v);
  const sidebarW = isSidebarOpen ? EXPANDED_W : COLLAPSED_W;

  // Measure header height once it’s rendered
  useLayoutEffect(() => {
    if (headerRef.current) {
      const h = headerRef.current.getBoundingClientRect().height;
      if (h && h !== headerH) setHeaderH(h);
    }
  }, []);

  return (
    <Flex
      className="stande-app"
      data-theme={isDark ? 'dark' : 'light'}
      minH="100vh"
      bg={a.page}
      overflowX="hidden"
      transition="background 0.2s"
    >
      {/* Header - full width, on top */}
      <Box
        as="header"
        ref={headerRef}
        position="fixed"
        top="0"
        left="0"
        right="0"
        zIndex="999"
        bg={a.chrome}
      >
        <Header
          isSidebarOpen={isSidebarOpen}
          onSidebarToggle={toggleSidebar}
          isDark={isDark}
          onToggleTheme={toggle}
        />
      </Box>

      {/* Sidebar - directly under header */}
      <Box
        as="nav"
        position="fixed"
        top={`${headerH}px`}
        left="0"
        h={`calc(100vh - ${headerH}px)`}
        w={`${sidebarW}px`}
        overflowX="visible"
        zIndex="900"
        bg={a.chrome}
        borderRight="1px solid"
        borderColor={a.border}
        transition="width 0.3s ease, background 0.2s"
      >
        <Sidebar collapsed={!isSidebarOpen} onToggle={toggleSidebar} isDark={isDark} />
      </Box>

      {/* Main content - right of sidebar, below header */}
      <Box
        as="main"
        pt={`${headerH + 45}px`}
        ml={`${sidebarW}px`}
        w={`calc(100% - ${sidebarW}px)`}
        transition="margin-left 0.3s ease, width 0.3s ease"
      >
        <Box px={{ base: 6, md: 8 }} pb={{ base: 6, md: 10 }}>
          {children}
        </Box>
      </Box>
    </Flex>
  );
};

export default Layout;
